import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AuthenticationActions, AuthenticationApiActions } from './authentication.actions';
import { AuthenticationService } from '../../services/authentication/authentication.service';
import { Store } from '@ngrx/store';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { Router } from '@angular/router';
import { StorageKey, StorageService } from '../../services/shared/storage.service';
import { User } from './model/user.model';

@Injectable()
export class AuthenticationEffects {
    constructor(
        private actions$: Actions,
        private authenticationService: AuthenticationService,
        private store: Store,
        private router: Router,
        private storageService: StorageService
    ) {}

    signIn$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthenticationApiActions.signIn),
            switchMap((action) =>
                this.authenticationService.signIn(action.credentials).pipe(
                    switchMap((user) => {
                        if (user.roles?.includes('admin') || user.roles?.includes('instructor')) {
                            void this.router.navigate(['calendar/admin']);
                        } else {
                            void this.router.navigate(['calendar/member']);
                        }

                        return [AuthenticationActions.saveUser({ user }), AuthenticationActions.signInSuccess({ user })];
                    }),
                    catchError((response) => of(AuthenticationActions.signInFailure({ error: response.error })))
                )
            )
        );
    });

    resetPassword$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthenticationApiActions.resetPassword),
            switchMap((action) =>
                this.authenticationService.resetPassword({ email: action.email }).pipe(
                    map(() => {
                        return AuthenticationActions.resetPasswordSuccess();
                    }),
                    catchError((response) => of(AuthenticationActions.resetPasswordFailure({ error: response.error })))
                )
            )
        );
    });

    signOut$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthenticationApiActions.signOut),
            switchMap(() =>
                this.authenticationService.signOut().pipe(
                    switchMap(() => {
                        void this.router.navigate(['/auth/sign-in']);
                        return [AuthenticationActions.clearStorage(), AuthenticationActions.signOutSuccess()];
                    })
                )
            )
        );
    });

    signUp$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthenticationApiActions.signUp),
            switchMap((action) =>
                this.authenticationService.signUp(action.userData).pipe(
                    map(() => AuthenticationActions.signUpSuccess()),
                    catchError((error) => of(AuthenticationActions.signUpFailure(error)))
                )
            )
        );
    });

    saveUserToStorage$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(AuthenticationActions.saveUser),
                tap(({ user }) => this.storageService.set<User>(StorageKey.USER, user))
            );
        },
        { dispatch: false }
    );

    clearStorage$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(AuthenticationActions.clearStorage, AuthenticationActions.authenticationFailed),
                tap(() => {
                    this.storageService.clear();
                })
            );
        },
        { dispatch: false }
    );
}
